// Production Telegram-like reaction effects for chat reactions.
// Ported from the styled mock: one shared canvas, sprite cache, object pool,
// themed effects per emoji and a longer hero animation so the effect is visible
// above chat messages instead of flashing instantly.
let canvas = null;
let ctx = null;
let width = 0;
let height = 0;
let dpr = 1;
let raf = 0;
let last = 0;
let quality = "balanced";

const prefersReduced = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

const QUALITY = {
  lite: { dpr: 1, factor: 0.7, max: 360, glow: false },
  balanced: { dpr: 1.35, factor: 1, max: 680, glow: true },
  ultra: { dpr: 1.6, factor: 1.35, max: 900, glow: true },
};

const cache = new Map();
const pool = [];
const active = [];

function ensureCanvas() {
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  if (canvas && ctx) return true;

  canvas = document.createElement("canvas");
  canvas.className = "telegram-reaction-fx-canvas";
  canvas.setAttribute("aria-hidden", "true");
  Object.assign(canvas.style, {
    position: "fixed",
    inset: "0",
    width: "100vw",
    height: "100vh",
    pointerEvents: "none",
    zIndex: "2147483600",
  });
  document.body.appendChild(canvas);
  ctx = canvas.getContext("2d", { alpha: true });
  resize();
  window.addEventListener("resize", resize, { passive: true });
  return Boolean(ctx);
}

function resize() {
  if (!canvas || !ctx || typeof window === "undefined") return;
  dpr = Math.min(window.devicePixelRatio || 1, QUALITY[quality].dpr);
  width = window.innerWidth || document.documentElement.clientWidth || 0;
  height = window.innerHeight || document.documentElement.clientHeight || 0;
  canvas.width = Math.max(1, Math.floor(width * dpr));
  canvas.height = Math.max(1, Math.floor(height * dpr));
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

function rand(min, max) { return Math.random() * (max - min) + min; }
function pick(items) { return items[(Math.random() * items.length) | 0]; }
function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
function easeOutBack(t) {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

function glowSprite(color, radius) {
  const key = `glow|${color}|${radius}`;
  if (cache.has(key)) return cache.get(key);
  const c = document.createElement("canvas");
  const size = Math.ceil(radius * 3);
  c.width = size;
  c.height = size;
  const g = c.getContext("2d");
  const cx = size / 2;
  const cy = size / 2;
  const grad = g.createRadialGradient(cx, cy, 0, cx, cy, radius);
  grad.addColorStop(0, color);
  grad.addColorStop(0.28, color);
  grad.addColorStop(1, "rgba(0,0,0,0)");
  g.fillStyle = grad;
  g.beginPath();
  g.arc(cx, cy, radius, 0, Math.PI * 2);
  g.fill();
  cache.set(key, c);
  return c;
}

function starSprite(color, size) {
  const key = `star|${color}|${size}`;
  if (cache.has(key)) return cache.get(key);
  const c = document.createElement("canvas");
  const s = Math.ceil(size * 5);
  c.width = s;
  c.height = s;
  const g = c.getContext("2d");
  g.translate(s / 2, s / 2);
  g.fillStyle = color;
  g.shadowColor = color;
  g.shadowBlur = size * 3;
  g.beginPath();
  for (let i = 0; i < 8; i += 1) {
    const r = i % 2 === 0 ? size : size * 0.35;
    const a = (Math.PI * 2 * i) / 8;
    g.lineTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  g.closePath();
  g.fill();
  cache.set(key, c);
  return c;
}

function emojiSprite(emoji, fontSize) {
  const key = `emoji|${emoji}|${fontSize}`;
  if (cache.has(key)) return cache.get(key);
  const c = document.createElement("canvas");
  const size = Math.ceil(fontSize * 2);
  c.width = size;
  c.height = size;
  const g = c.getContext("2d");
  g.font = `${fontSize}px system-ui, Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif`;
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.shadowColor = "rgba(0,0,0,.28)";
  g.shadowBlur = 8;
  g.fillText(emoji, size / 2, size / 2 + 1);
  cache.set(key, c);
  return c;
}

function obtain() {
  return pool.pop() || {
    type: "dot", x: 0, y: 0, vx: 0, vy: 0, ax: 0, ay: 0, drag: 0.99,
    life: 1, decay: 0.02, size: 4, rotation: 0, spin: 0, color: "#fff", sprite: null,
    meta: null, age: 0, startX: 0, startY: 0, endX: 0, endY: 0, duration: 1,
  };
}
function recycle(p) { p.sprite = null; p.meta = null; pool.push(p); }
function spawn(patch) {
  if (active.length >= QUALITY[quality].max) recycle(active.shift());
  const p = obtain();
  Object.assign(p, {
    type: "dot", x: 0, y: 0, vx: 0, vy: 0, ax: 0, ay: 0, drag: 0.99,
    life: 1, decay: 0.02, size: 4, rotation: 0, spin: 0, color: "#fff", sprite: null,
    meta: null, age: 0, startX: 0, startY: 0, endX: 0, endY: 0, duration: 1,
  }, patch);
  active.push(p);
  return p;
}

function burst(x, y, count, cfg) {
  const total = Math.max(1, Math.round(count * QUALITY[quality].factor * (prefersReduced() ? 0.55 : 1)));
  for (let i = 0; i < total; i += 1) {
    const angle = rand(cfg.angleMin ?? 0, cfg.angleMax ?? Math.PI * 2);
    const speed = rand(cfg.speedMin, cfg.speedMax);
    const size = rand(cfg.sizeMin, cfg.sizeMax);
    const type = typeof cfg.type === "function" ? cfg.type() : (cfg.type || "dot");
    const color = cfg.colors ? pick(cfg.colors) : "#fff";
    let sprite = null;
    if (type === "glow") sprite = glowSprite(color, size * 2.3);
    if (type === "star") sprite = starSprite(color, size * 1.7);
    if (type === "emoji") sprite = emojiSprite(cfg.emoji || "✨", size * 4.2);
    spawn({
      type, x, y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed + (cfg.vyOffset || 0),
      ax: cfg.ax ?? 0,
      ay: cfg.ay ?? 0,
      drag: cfg.drag ?? 0.988,
      life: 1,
      decay: rand(cfg.decayMin, cfg.decayMax),
      size,
      rotation: rand(0, Math.PI * 2),
      spin: rand(cfg.spinMin ?? -0.14, cfg.spinMax ?? 0.14),
      color,
      sprite,
      meta: cfg.meta || null,
    });
  }
}

function flash(x, y, color, scale = 1) { spawn({ type: "flash", x, y, size: 34 * scale, color, life: 1, decay: 0.05 }); }
function ring(x, y, color, scale = 1, line = 2.1) { spawn({ type: "ring", x, y, size: 12 * scale, color, life: 1, decay: 0.032, meta: { grow: 7.6 * scale, line } }); }
function waveRing(x, y, color, scale = 1) { ring(x, y, color, scale, 2.4); ring(x, y, color.replace("0.9", "0.55"), scale * 0.7, 1.6); }

function heroRise(x, y, emoji, theme) {
  return spawn({
    type: "hero", startX: x, startY: y, endX: x + rand(-8, 8), endY: y - rand(100, 138), x, y,
    duration: 0.9, age: 0, life: 1, decay: 0.007, sprite: emojiSprite(emoji, 62),
    rotation: rand(-0.05, 0.05), spin: rand(-0.02, 0.02), meta: { emoji, theme, burstDone: false, trailTick: 0 },
  });
}

function fireStyle(x, y) {
  flash(x, y, "rgba(255,198,88,.98)", 1.08); waveRing(x, y, "rgba(255,214,120,0.92)", 1.02);
  burst(x, y, 54, { type: () => Math.random() > 0.78 ? "star" : "glow", speedMin: 1.6, speedMax: 7.3, sizeMin: 1.8, sizeMax: 4.8, decayMin: 0.008, decayMax: 0.017, ay: 0.026, drag: 0.988, colors: ["rgba(255,248,204,.95)", "rgba(255,219,96,.93)", "rgba(255,158,50,.92)", "rgba(255,96,0,.90)"] });
  burst(x, y, 38, { type: "dot", angleMin: -Math.PI * 0.95, angleMax: -Math.PI * 0.05, speedMin: 0.6, speedMax: 3, sizeMin: 1.1, sizeMax: 2.4, decayMin: 0.006, decayMax: 0.012, ay: -0.01, drag: 0.994, colors: ["rgba(255,240,185,.93)", "rgba(255,184,72,.90)", "rgba(255,122,40,.88)"] });
  burst(x, y, 14, { type: "emoji", emoji: "🔥", angleMin: -Math.PI * 0.84, angleMax: -Math.PI * 0.16, speedMin: 0.5, speedMax: 2.2, sizeMin: 5, sizeMax: 6.6, decayMin: 0.01, decayMax: 0.017, ay: -0.014, drag: 0.992 });
  heroRise(x, y, "🔥", "fire");
}
function loveStyle(x, y) {
  flash(x, y, "rgba(255,190,214,.95)", 0.96); ring(x, y, "rgba(255,224,236,0.92)", 0.95, 2.3); ring(x, y, "rgba(255,182,211,0.72)", 0.72, 1.6);
  burst(x, y, 24, { type: () => Math.random() > 0.6 ? "emoji" : "glow", emoji: "❤️", speedMin: 0.4, speedMax: 2.3, sizeMin: 1.9, sizeMax: 4.2, decayMin: 0.01, decayMax: 0.02, ay: -0.026, drag: 0.992, colors: ["rgba(255,248,252,.96)", "rgba(255,201,222,.92)", "rgba(255,125,170,.90)"] });
  burst(x, y, 24, { type: "dot", speedMin: 0.4, speedMax: 1.6, sizeMin: 0.9, sizeMax: 2, decayMin: 0.01, decayMax: 0.019, ay: -0.018, drag: 0.993, colors: ["rgba(255,255,255,.86)", "rgba(255,228,238,.86)", "rgba(255,173,205,.80)"] });
  heroRise(x, y, "❤️", "love");
}
function wowStyle(x, y, emoji = "🤩") {
  flash(x, y, "rgba(255,240,160,.96)", 1); waveRing(x, y, "rgba(255,246,190,0.92)", 0.98);
  burst(x, y, 32, { type: () => Math.random() > 0.48 ? "star" : "glow", speedMin: 0.9, speedMax: 4.2, sizeMin: 1.8, sizeMax: 4.5, decayMin: 0.009, decayMax: 0.018, ay: 0.01, drag: 0.989, colors: ["rgba(255,252,214,.96)", "rgba(255,221,85,.92)", "rgba(126,199,255,.88)", "rgba(173,132,255,.90)"] });
  burst(x, y, 12, { type: "emoji", emoji: "✨", speedMin: 0.5, speedMax: 2, sizeMin: 4.8, sizeMax: 6.3, decayMin: 0.012, decayMax: 0.02, ay: -0.012, drag: 0.992 });
  heroRise(x, y, emoji, "wow");
}
function electricStyle(x, y) {
  flash(x, y, "rgba(255,238,125,.98)", 1.02); ring(x, y, "rgba(255,246,186,0.93)", 1, 2);
  burst(x, y, 34, { type: () => Math.random() > 0.72 ? "emoji" : "glow", emoji: "⚡", angleMin: -Math.PI * 0.84, angleMax: -Math.PI * 0.16, speedMin: 1, speedMax: 4.8, sizeMin: 1.6, sizeMax: 3.9, decayMin: 0.01, decayMax: 0.019, ay: 0.006, drag: 0.988, colors: ["rgba(255,250,208,.98)", "rgba(255,231,90,.94)", "rgba(157,226,255,.88)"] });
  burst(x, y, 26, { type: "dot", angleMin: -Math.PI * 0.88, angleMax: -Math.PI * 0.12, speedMin: 1.2, speedMax: 5.2, sizeMin: 0.9, sizeMax: 2, decayMin: 0.008, decayMax: 0.016, ay: 0.004, drag: 0.987, colors: ["rgba(255,248,194,.94)", "rgba(255,228,72,.90)", "rgba(127,224,255,.82)"] });
  heroRise(x, y, "⚡", "electric");
}
function partyStyle(x, y) {
  flash(x, y, "rgba(255,237,170,.90)", 0.98); ring(x, y, "rgba(255,255,255,0.84)", 0.96, 2.1);
  burst(x, y, 42, { type: () => Math.random() > 0.82 ? "star" : "dot", speedMin: 1, speedMax: 4.8, sizeMin: 1.4, sizeMax: 3.6, decayMin: 0.01, decayMax: 0.021, ay: 0.019, drag: 0.988, colors: ["rgba(255,216,70,.94)", "rgba(255,113,205,.92)", "rgba(95,186,255,.90)", "rgba(154,130,255,.90)", "rgba(255,255,255,.88)"] });
  burst(x, y, 10, { type: "emoji", emoji: "🎉", speedMin: 0.5, speedMax: 2.2, sizeMin: 5.6, sizeMax: 6.8, decayMin: 0.012, decayMax: 0.02, ay: -0.012, drag: 0.992 });
  heroRise(x, y, "🎉", "party");
}
function genericStyle(x, y, emoji) {
  flash(x, y, "rgba(168,157,232,.70)", 0.82); ring(x, y, "rgba(90,234,160,.72)", 0.85, 1.8);
  burst(x, y, 20, { type: () => Math.random() > 0.7 ? "star" : "glow", speedMin: 0.8, speedMax: 3.8, sizeMin: 1.3, sizeMax: 3.8, decayMin: 0.01, decayMax: 0.02, ay: 0.01, drag: 0.989, colors: ["rgba(255,255,255,.92)", "rgba(168,157,232,.86)", "rgba(90,234,160,.80)"] });
  heroRise(x, y, emoji, "generic");
}

function secondaryBurst(hero) {
  const { x, y } = hero;
  const theme = hero.meta?.theme || "generic";
  const palettes = {
    fire: ["rgba(255,247,202,.95)", "rgba(255,213,87,.92)", "rgba(255,144,44,.90)", "rgba(255,95,0,.88)"],
    love: ["rgba(255,250,252,.95)", "rgba(255,199,220,.92)", "rgba(255,114,161,.88)"],
    wow: ["rgba(255,252,214,.96)", "rgba(255,221,85,.92)", "rgba(126,199,255,.88)", "rgba(173,132,255,.90)"],
    electric: ["rgba(255,251,202,.97)", "rgba(255,232,82,.92)", "rgba(142,225,255,.86)"],
    party: ["rgba(255,216,70,.94)", "rgba(255,113,205,.92)", "rgba(95,186,255,.90)", "rgba(154,130,255,.90)", "rgba(255,255,255,.88)"],
    generic: ["rgba(255,255,255,.90)", "rgba(168,157,232,.84)", "rgba(90,234,160,.80)"],
  };
  burst(x, y, 30, { type: () => Math.random() > 0.55 ? "star" : "glow", speedMin: 0.6, speedMax: 3.4, sizeMin: 1.2, sizeMax: 3.5, decayMin: 0.012, decayMax: 0.023, ay: 0.01, drag: 0.989, colors: palettes[theme] || palettes.generic });
}

function trailSparkles(x, y, theme) {
  const palettes = {
    fire: ["rgba(255,244,186,.94)", "rgba(255,184,70,.88)", "rgba(255,116,35,.82)"],
    love: ["rgba(255,255,255,.90)", "rgba(255,215,230,.88)", "rgba(255,154,191,.82)"],
    wow: ["rgba(255,248,192,.94)", "rgba(255,221,82,.90)", "rgba(129,196,255,.82)"],
    electric: ["rgba(255,250,206,.95)", "rgba(255,228,84,.90)", "rgba(144,224,255,.84)"],
    party: ["rgba(255,217,70,.92)", "rgba(255,113,205,.90)", "rgba(94,186,255,.86)"],
    generic: ["rgba(255,255,255,.88)", "rgba(168,157,232,.78)", "rgba(90,234,160,.78)"],
  };
  burst(x, y, 8, { type: () => Math.random() > 0.52 ? "glow" : "dot", speedMin: 0.2, speedMax: 1.1, sizeMin: 0.8, sizeMax: 2, decayMin: 0.02, decayMax: 0.036, ay: 0.004, drag: 0.987, colors: palettes[theme] || palettes.generic });
}

const PRESETS = {
  "🔥": fireStyle,
  "❤️": loveStyle,
  "🤩": wowStyle,
  "😮": (x, y) => wowStyle(x, y, "😮"),
  "⚡": electricStyle,
  "🎉": partyStyle,
};

function update(dt) {
  for (let i = active.length - 1; i >= 0; i -= 1) {
    const p = active[i];
    p.age += dt;
    if (p.type === "hero") {
      const t = clamp(p.age / p.duration, 0, 1);
      const e = easeOutBack(t);
      p.x = p.startX + (p.endX - p.startX) * e;
      p.y = p.startY + (p.endY - p.startY) * easeOutCubic(t) - Math.sin(t * Math.PI) * 10;
      p.rotation += p.spin * dt * 60;
      p.life -= p.decay * dt * 60;
      p.meta.trailTick += 1;
      if (p.meta.trailTick >= 3) { p.meta.trailTick = 0; trailSparkles(p.x, p.y + 5, p.meta.theme); }
      if (t > 0.72 && !p.meta.burstDone) { p.meta.burstDone = true; secondaryBurst(p); }
    } else {
      p.vx += p.ax * dt;
      p.vy += p.ay * dt;
      p.vx *= p.drag;
      p.vy *= p.drag;
      p.x += p.vx * dt * 60;
      p.y += p.vy * dt * 60;
      p.rotation += p.spin * dt * 60;
      p.life -= p.decay * dt * 60;
      if (p.type === "ring") p.size += (p.meta?.grow || 7) * dt * 60;
      if (p.type === "flash") p.size += 2.3 * dt * 60;
    }
    if (p.life <= 0) { recycle(p); active.splice(i, 1); }
  }
}

function drawSpriteParticle(p, baseScale) {
  if (!p.sprite) return;
  const alpha = clamp(p.life, 0, 1);
  ctx.globalAlpha = alpha;
  const w = p.sprite.width;
  const h = p.sprite.height;
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rotation);
  ctx.scale(baseScale, baseScale);
  ctx.drawImage(p.sprite, -w / 2, -h / 2);
  ctx.restore();
}

function drawParticle(p) {
  const alpha = clamp(p.life, 0, 1);
  ctx.globalAlpha = alpha;
  switch (p.type) {
    case "dot":
      ctx.fillStyle = p.color;
      if (QUALITY[quality].glow) { ctx.shadowColor = p.color; ctx.shadowBlur = 8; }
      ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill(); ctx.shadowBlur = 0; break;
    case "glow": drawSpriteParticle(p, 0.62 + (p.size / 8)); break;
    case "star": drawSpriteParticle(p, 0.72 + (p.size / 8)); break;
    case "emoji": drawSpriteParticle(p, 0.38 + (1 - alpha) * 0.18); break;
    case "ring": ctx.strokeStyle = p.color; ctx.lineWidth = p.meta?.line || 2; ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.stroke(); break;
    case "flash": {
      const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size);
      grad.addColorStop(0, p.color); grad.addColorStop(0.28, p.color); grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad; ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill(); break;
    }
    case "hero": {
      if (!p.sprite) break;
      const t = clamp(p.age / p.duration, 0, 1);
      let scale = 0.52 + easeOutBack(t) * 0.68;
      if (t > 0.76) scale *= (1 - ((t - 0.76) / 0.24) * 0.12);
      ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rotation); ctx.scale(scale, scale); ctx.drawImage(p.sprite, -p.sprite.width / 2, -p.sprite.height / 2); ctx.restore(); break;
    }
    default: break;
  }
}

function render(now) {
  if (!ctx) return;
  const dt = Math.min((now - last) / 1000, 0.033);
  last = now;
  ctx.clearRect(0, 0, width, height);
  update(dt);
  for (let i = 0; i < active.length; i += 1) drawParticle(active[i]);
  ctx.globalAlpha = 1;
  if (active.length) raf = window.requestAnimationFrame(render);
  else raf = 0;
}

export function setReactionEffectQuality(next) {
  if (!QUALITY[next]) return;
  quality = next;
  resize();
}

export function triggerReactionEffectAt(x, y, emoji = "❤️") {
  if (!ensureCanvas() || prefersReduced()) return;
  const safeX = Number.isFinite(Number(x)) ? Number(x) : width / 2;
  const safeY = Number.isFinite(Number(y)) ? Number(y) : height / 2;
  const normalizedEmoji = String(emoji || "❤️");
  const fn = PRESETS[normalizedEmoji] || ((px, py) => genericStyle(px, py, normalizedEmoji));
  fn(safeX, safeY);
  if (navigator.vibrate) navigator.vibrate(14);
  if (!raf) { last = performance.now(); raf = window.requestAnimationFrame(render); }
}

export function triggerReactionEffectFromElement(element, emoji = "❤️") {
  if (!element || typeof element.getBoundingClientRect !== "function") return;
  const rect = element.getBoundingClientRect();
  triggerReactionEffectAt(rect.left + rect.width / 2, rect.top + rect.height / 2, emoji);
}
